import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  /**
   * Auto-open the cart drawer after an item is added.
   *
   * This theme can render multiple cart drawer instances (e.g. desktop + mobile header).
   * If more than one instance calls `showModal()` it can throw and/or corrupt body scroll-lock state.
   *
   * @param {import('./events').CartAddEvent} event
   */
  #handleCartAdd = (event) => {
    if (!this.hasAttribute('auto-open')) return;

    // Only auto-open for successful add-to-cart actions (not quantity changes).
    const { didError, source } = event?.detail?.data || {};
    if (didError) return;
    if (source && source !== 'product-form-component') return;

    // If multiple drawers exist, only allow the preferred visible instance to open.
    const preferred = CartDrawerComponent.getPreferredAutoOpenInstance();
    if (preferred && preferred !== this) return;

    this.showDialog();
  };

  static getPreferredAutoOpenInstance() {
    /** @type {CartDrawerComponent[]} */
    const candidates = Array.from(document.querySelectorAll('cart-drawer-component[auto-open]')).filter(
      (el) => el instanceof CartDrawerComponent
    );

    if (candidates.length <= 1) return candidates[0] || null;

    const isVisible = (/** @type {Element | null} */ el) => {
      if (!(el instanceof HTMLElement)) return false;
      if (el.hidden) return false;

      const style = window.getComputedStyle(el);
      if (style.display === 'none' || style.visibility === 'hidden') return false;

      // `display:none` yields no rects; this works well for responsive header variants.
      const rect = el.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0;
    };

    const getTrigger = (/** @type {CartDrawerComponent} */ drawer) =>
      drawer.querySelector('button[on\\:click="/open"], .header-actions__action');

    const visible = candidates.filter((drawer) => isVisible(getTrigger(drawer)) || isVisible(drawer));
    if (visible.length === 1) return visible[0];
    if (visible.length > 1) return visible[visible.length - 1];

    return candidates[candidates.length - 1] || null;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
